import {selectIsCodeComponentCanvas} from '@packages/systems/component-canvas/universal';
import {getCanvasBarHeight} from '@packages/systems/design-system/constants/DesignerUI';
import draggable from '@packages/systems/design-system/logic/draggable';
import {
  selectCanvasBarPosition,
  selectIsPreviewMode,
  selectLeftSidebarWidth,
  selectTopBarHeight,
} from '@packages/systems/designer-selectors';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import type {CanvasBarPosition} from '@packages/systems/designer/ui';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {View} from '@packages/systems/spring';
import {Layer} from '@packages/systems/ui/overlay';
import {emotionStyled} from '@packages/utilities/styled';
import {clamp} from 'lodash';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';
import type {DraggableData, DraggableEvent} from 'react-draggable';
import {DraggableCore} from 'react-draggable';
import {CodeComponentEditor} from './CodeComponentEditor';
import {SourceFileTree} from './SourceFileTree';
import {
  codeComponentEditorPaneMove,
  codeComponentEditorPaneResize,
} from './flux/actionCreators';
import {
  initialEditorPaneState,
  type PanePosition,
  type PaneSize,
} from './flux/CodeComponentUIState';
import {
  selectCodeEditorPane,
  useCodeComponentDispatch,
  useCodeComponentSelector,
} from './flux/CodeLibrariesSlice';

const ResizeHandleRight = draggable(
  emotionStyled('div')({
    width: '6px',
    cursor: 'ew-resize',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 3,
  })
);

const ResizeHandleBottom = draggable(
  emotionStyled('div')({
    height: '6px',
    cursor: 'ns-resize',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 3,
  })
);

const ResizeHandleCorner = draggable(
  emotionStyled('div')({
    width: '16px',
    height: '16px',
    cursor: 'nwse-resize',
    position: 'absolute',
    bottom: 0,
    right: 0,
    zIndex: 4,
  })
);

const getViewportSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

const DRAG_GRAB_MARGIN = 100;
const PANE_MIN_WIDTH = 320;
const PANE_MIN_HEIGHT = 300;
const PANE_GUTTER = 8;

type DragBounds = {minLeft: number; minTop: number; maxRight: number};

const getMinTopBound = (
  position: CanvasBarPosition,
  topBarHeight: number
): number =>
  position === 'top'
    ? topBarHeight + getCanvasBarHeight(position)
    : topBarHeight;

export const getCodeEditorDragBounds = ({
  canvasBarPosition,
  workspaceLeftInset,
  workspaceRightInset,
  topBarHeight,
  viewportWidth,
}: {
  canvasBarPosition: CanvasBarPosition;
  workspaceLeftInset: number;
  workspaceRightInset: number;
  topBarHeight: number;
  viewportWidth: number;
}): DragBounds => {
  const minLeft = workspaceLeftInset;
  return {
    minLeft,
    minTop: getMinTopBound(canvasBarPosition, topBarHeight),
    maxRight: Math.max(minLeft, viewportWidth - workspaceRightInset),
  };
};

export const getDefaultCodeEditorPosition = ({
  bounds,
  paneWidth,
  gutter = PANE_GUTTER,
}: {
  bounds: DragBounds;
  paneWidth: number;
  gutter?: number;
}): PanePosition => ({
  x: Math.max(bounds.minLeft, bounds.maxRight - paneWidth - gutter),
  y: bounds.minTop + gutter,
});

type CodeEditorFloatingPaneProps = {
  workspaceLeftInset?: number;
  workspaceRightInset?: number;
};

export const CodeEditorFloatingPane = ({
  workspaceLeftInset,
  workspaceRightInset = 0,
}: CodeEditorFloatingPaneProps) => {
  const dispatch = useCodeComponentDispatch();
  const editorPane = useCodeComponentSelector(selectCodeEditorPane);
  const isCodeComponentCanvas = useCodeComponentSelector(
    selectIsCodeComponentCanvas
  );
  const isPreviewMode = useCodeComponentSelector(selectIsPreviewMode);
  const isMultifileEnabled = useFeatureFlag(FEATURE_FLAGS.VCC_MULTIFILE);
  const leftSidebarWidth = useCodeComponentSelector(selectLeftSidebarWidth);
  const canvasBarPosition = useDesignerSelector(selectCanvasBarPosition);
  const topBarHeight = useDesignerSelector(selectTopBarHeight);
  const resolvedWorkspaceLeftInset = workspaceLeftInset ?? leftSidebarWidth;

  const nodeRef = useRef<HTMLDivElement | null>(null);
  const positionRef = useRef<PanePosition>(editorPane.position);
  const sizeRef = useRef<PaneSize>(editorPane.size);
  const resizeStartSizeRef = useRef<PaneSize>(editorPane.size);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const dragBoundsRef = useRef<DragBounds>({
    minLeft: 0,
    minTop: 0,
    maxRight: window.innerWidth,
  });

  const [isDragging, setIsDragging] = useState(false);
  const [resizing, setResizing] = useState(false);

  const getDragBounds = useCallback(
    () =>
      getCodeEditorDragBounds({
        canvasBarPosition,
        workspaceLeftInset: resolvedWorkspaceLeftInset,
        workspaceRightInset,
        topBarHeight,
        viewportWidth: window.innerWidth,
      }),
    [
      canvasBarPosition,
      workspaceRightInset,
      resolvedWorkspaceLeftInset,
      topBarHeight,
    ]
  );

  const resolvePosition = useCallback((): PanePosition => {
    if (editorPane.position !== initialEditorPaneState.position) {
      return editorPane.position;
    }
    return getDefaultCodeEditorPosition({
      bounds: getDragBounds(),
      paneWidth: editorPane.size.width,
    });
  }, [editorPane.position, editorPane.size.width, getDragBounds]);

  const clampPosition = useCallback((x: number, y: number) => {
    const viewport = getViewportSize();
    const {minLeft, minTop, maxRight} = dragBoundsRef.current;
    const paneWidth = sizeRef.current.width;
    return {
      x: clamp(x, minLeft, Math.max(minLeft, maxRight - paneWidth)),
      y: clamp(y, minTop, viewport.height - DRAG_GRAB_MARGIN),
    };
  }, []);

  const clampSize = useCallback((size: PaneSize, position: PanePosition) => {
    const viewport = getViewportSize();
    const {maxRight} = dragBoundsRef.current;
    const maxWidth = Math.max(PANE_MIN_WIDTH, maxRight - position.x);
    const maxHeight = Math.max(
      PANE_MIN_HEIGHT,
      viewport.height - position.y - 2
    );

    return {
      width: clamp(size.width, PANE_MIN_WIDTH, maxWidth),
      height: clamp(size.height, PANE_MIN_HEIGHT, maxHeight),
    };
  }, []);

  const applyPosition = useCallback((pos: PanePosition) => {
    positionRef.current = pos;
    if (nodeRef.current) {
      nodeRef.current.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
    }
  }, []);

  const applySize = useCallback((size: PaneSize) => {
    sizeRef.current = size;
    if (nodeRef.current) {
      nodeRef.current.style.width = `${size.width}px`;
      nodeRef.current.style.height = `${size.height}px`;
    }
  }, []);

  const handleDragStart = useCallback(
    (_: DraggableEvent, _data: DraggableData) => {
      isDraggingRef.current = true;
      dragBoundsRef.current = getDragBounds();
      setIsDragging(true);
    },
    [getDragBounds]
  );

  const handleDrag = useCallback(
    (_: DraggableEvent, data: DraggableData) => {
      const cur = positionRef.current;
      const newPos = clampPosition(cur.x + data.deltaX, cur.y + data.deltaY);
      positionRef.current = newPos;
      applyPosition(newPos);
    },
    [clampPosition, applyPosition]
  );

  const handleDragStop = useCallback(
    (_: DraggableEvent, data: DraggableData) => {
      const cur = positionRef.current;
      const newPos = clampPosition(cur.x + data.deltaX, cur.y + data.deltaY);
      applyPosition(newPos);
      isDraggingRef.current = false;
      setIsDragging(false);
      dispatch(codeComponentEditorPaneMove(newPos));
    },
    [clampPosition, applyPosition, dispatch]
  );

  const handleResizeStart = useCallback(() => {
    isResizingRef.current = true;
    resizeStartSizeRef.current = {...sizeRef.current};
    dragBoundsRef.current = getDragBounds();
    setResizing(true);
  }, [getDragBounds]);

  const handleResizeRight = useCallback(
    (delta: PanePosition) => {
      const startSize = resizeStartSizeRef.current;
      const maxWidth = Math.max(
        PANE_MIN_WIDTH,
        dragBoundsRef.current.maxRight - positionRef.current.x
      );
      const newWidth = clamp(
        startSize.width + delta.x,
        PANE_MIN_WIDTH,
        maxWidth
      );
      applySize({...sizeRef.current, width: newWidth});
    },
    [applySize]
  );

  const handleResizeBottom = useCallback(
    (delta: PanePosition) => {
      const startSize = resizeStartSizeRef.current;
      const viewport = getViewportSize();
      const maxHeight = viewport.height - positionRef.current.y - 2;
      const newHeight = clamp(
        startSize.height + delta.y,
        PANE_MIN_HEIGHT,
        maxHeight
      );
      applySize({...sizeRef.current, height: newHeight});
    },
    [applySize]
  );

  const handleResizeCorner = useCallback(
    (delta: PanePosition) => {
      const startSize = resizeStartSizeRef.current;
      const viewport = getViewportSize();
      const maxWidth = Math.max(
        PANE_MIN_WIDTH,
        dragBoundsRef.current.maxRight - positionRef.current.x
      );
      const maxHeight = viewport.height - positionRef.current.y - 2;
      const newWidth = clamp(
        startSize.width + delta.x,
        PANE_MIN_WIDTH,
        maxWidth
      );
      const newHeight = clamp(
        startSize.height + delta.y,
        PANE_MIN_HEIGHT,
        maxHeight
      );
      applySize({width: newWidth, height: newHeight});
    },
    [applySize]
  );

  const handleResizeEnd = useCallback(() => {
    dispatch(codeComponentEditorPaneResize(sizeRef.current));
    isResizingRef.current = false;
    setResizing(false);
  }, [dispatch]);

  const handleNodeRefChange = useCallback(
    (element: HTMLDivElement | null) => {
      // Whenever the underlying element changes or mounts, we set it position and size
      if (element && nodeRef.current !== element) {
        nodeRef.current = element;
        dragBoundsRef.current = getDragBounds();
        const {size} = editorPane;
        const position = resolvePosition();
        const clampedPos = clampPosition(position.x, position.y);
        applyPosition(clampedPos);
        applySize(clampSize(size, clampedPos));
      }
    },
    [
      applyPosition,
      applySize,
      clampPosition,
      clampSize,
      editorPane,
      getDragBounds,
      resolvePosition,
    ]
  );

  useLayoutEffect(() => {
    if (!isDraggingRef.current) {
      applyPosition(resolvePosition());
    }
  }, [resolvePosition, applyPosition]);

  useLayoutEffect(() => {
    if (!isResizingRef.current) {
      applySize(editorPane.size);
    }
  }, [editorPane.size, applySize]);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (isDraggingRef.current || isResizingRef.current) return;

      dragBoundsRef.current = getDragBounds();

      const {x: curX, y: curY} = positionRef.current;
      const clampedPos = clampPosition(curX, curY);
      const clampedSize = clampSize(sizeRef.current, clampedPos);
      if (clampedPos.x !== curX || clampedPos.y !== curY) {
        applyPosition(clampedPos);
        dispatch(codeComponentEditorPaneMove(clampedPos));
      }
      if (
        clampedSize.width !== sizeRef.current.width ||
        clampedSize.height !== sizeRef.current.height
      ) {
        applySize(clampedSize);
        dispatch(codeComponentEditorPaneResize(clampedSize));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [
    clampPosition,
    clampSize,
    applyPosition,
    applySize,
    dispatch,
    getDragBounds,
  ]);

  if (!isCodeComponentCanvas || !editorPane.isVisible || isPreviewMode) {
    return null;
  }

  return (
    <Layer>
      <View
        position="fixed"
        top={0}
        left={0}
        width={isDragging || resizing ? '100vw' : undefined}
        height={isDragging || resizing ? '100vh' : 0}
      >
        <DraggableCore
          handle="[data-code-component-editor-drag-handle]"
          nodeRef={nodeRef}
          onStart={handleDragStart}
          onDrag={handleDrag}
          onStop={handleDragStop}
        >
          <div
            ref={handleNodeRefChange}
            data-automation-id="vcc-code-editor-pane"
          >
            <CodeComponentEditor
              isSelectable={!(isDragging || resizing)}
              sourceTree={isMultifileEnabled ? <SourceFileTree /> : undefined}
            />
            <ResizeHandleRight
              dragging={resizing}
              mode="delta"
              onDragStart={handleResizeStart}
              onDrag={handleResizeRight}
              onDragEnd={handleResizeEnd}
            />
            <ResizeHandleBottom
              dragging={resizing}
              mode="delta"
              onDragStart={handleResizeStart}
              onDrag={handleResizeBottom}
              onDragEnd={handleResizeEnd}
            />
            <ResizeHandleCorner
              dragging={resizing}
              mode="delta"
              onDragStart={handleResizeStart}
              onDrag={handleResizeCorner}
              onDragEnd={handleResizeEnd}
            />
          </div>
        </DraggableCore>
      </View>
    </Layer>
  );
};
